import { useState, useCallback } from "react";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import { useAuth } from "@/providers/auth-provider";
import { useRaid } from "@/providers/raid-provider";
import { RouteNames } from "@/types/route-names";
import { PokeType } from "types/pokemon/poke-type";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { StrokedText } from "@/components/ui/text/stroked-text";
import classNames from "classnames";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { Direction } from "types/battle-engine/battle";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { RaidDifficulty } from "types/raids/types";
import { RaidConfig, RAIDS } from "game/data/raids";
import { RaidDetailModal } from "./raid-detail-modal";
import { getPokemonSpriteScale } from "game/data/pokemon/sprite-scale-overrides";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";

export const RaidSelectView = () => {
  const { setCurrentRoute, previousRoute } = useSPAContext();
  const { getAccessToken, state: authState } = useAuth();
  const { createRaidLobby } = useRaid();

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const [isCreating, setIsCreating] = useState(false);
  const [selectedRaid, setSelectedRaid] = useState<RaidConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Opens the raid detail modal
   */
  const handleOpenRaidDetail = (raid: RaidConfig) => {
    setSelectedRaid(raid);
    setError(null);
  };

  /**
   * Closes the raid detail modal
   */
  const handleCloseModal = () => {
    if (!isCreating) {
      setSelectedRaid(null);
    }
  };

  /**
   * Handle raid start from modal (creates lobby)
   */
  const handleStartRaid = async (difficulty: RaidDifficulty) => {
    if (!selectedRaid) return;

    const token = getAccessToken();
    if (!token || authState.status !== "authenticated") {
      setError("Authentication required");
      return;
    }

    const playerId = authState.user?.telegram_id;
    if (!playerId) {
      setError("Player ID not found");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const result = await createRaidLobby(
        token,
        playerId,
        selectedRaid,
        difficulty,
      );

      if (result.success) {
        setCurrentRoute(RouteNames.RAID_LOBBY);
      } else {
        setError(result.error || "Failed to create raid");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error("[RaidSelectView] Create error:", err);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full h-full max-h-full pt-4 bg-pk-blue-3 pb-32 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Dot pattern overlay - large dots fading out toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 7px, transparent 7px)",
            backgroundSize: "14px 14px",
            opacity: 0.028,
            maskImage: "linear-gradient(to bottom, black 0%, transparent 30%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, transparent 60%)",
          }}
        />
        {/* Dot pattern overlay - small dots fading in toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 1.5px, transparent 1.5px)",
            backgroundSize: "4px 4px",
            opacity: 0.048,
            maskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
          }}
        />
      </div>

      {/* Top Bar */}
      <div className="flex flex-col px-2 gap-2 relative z-10">
        <div className="relative z-10 flex items-center justify-between ">
          <div className="flex flex-col   p-2">
            {/* Top bar with tags */}
            <div className="flex items-center justify-between mb-4 ">
              <div className="flex items-center gap-2">
                <span className="bg-pk-psychic text-white text-[10px] font-black uppercase px-2 py-1 tracking-wider">
                  CO-OP
                </span>
                <span className="bg-pk-gray-2 text-pk-gray-4 text-[10px] font-bold uppercase px-2 py-1">
                  1-4 PLAYERS
                </span>
              </div>
            </div>

            {/* Title */}
            <div className="relative ">
              <h1 className="text-6xl font-black text-white uppercase tracking-tight leading-none drop-shadow-lg">
                RAIDS
              </h1>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <StandardFrame variant="red" className="w-full">
            <div className="p-2 text-center text-white font-medium text-sm">
              {error}
            </div>
          </StandardFrame>
        )}

        {/* Main Content */}
        <div className="h-full flex flex-col max-h-[70vh]">
          <div className="grid grid-cols-1 gap-6 w-full mx-auto rounded-lg max-h-full overflow-y-auto p-2">
            {RAIDS.map((raid) => (
              <RaidTypeCard
                key={raid.type}
                raid={raid}
                isSelected={selectedRaid?.type === raid.type}
                isDisabled={isCreating}
                onClick={() => handleOpenRaidDetail(raid)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Raid Detail Modal */}
      {selectedRaid && (
        <RaidDetailModal
          raid={selectedRaid}
          isOpen={!!selectedRaid}
          onClose={handleCloseModal}
          onStart={handleStartRaid}
          isCreating={isCreating}
        />
      )}
    </div>
  );
};

/**
 * Individual raid type card component
 */
interface RaidTypeCardProps {
  raid: RaidConfig;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
}

const RaidTypeCard = ({
  raid,
  isSelected,
  isDisabled,
  onClick,
}: RaidTypeCardProps) => {
  const bossPokes = raid.bosses.filter(
    (boss) => boss.rarity === "rare" || boss.rarity === "uncommon",
  );

  const bossPokeNames = bossPokes.map((boss) =>
    parsePokeNameForSprite(boss.name),
  );

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={classNames(
        "group relative overflow-visible rounded-lg transition-all duration-200 min-h-[100px]",
        "border-4 shadow-cartoon-sm hover:shadow-cartoon",
        {
          "border-pk-yellow-3 scale-95": isSelected,
          [`border-pk-gray-4 hover:border-pk-gray-3`]: !isSelected,
          "opacity-50 cursor-not-allowed": isDisabled && !isSelected,
          "active:scale-95": !isDisabled,
        },
      )}
      style={{
        backgroundImage: `url("/assets/raids/backgrounds/${raid.type.toLowerCase()}.webp")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Background Gradient */}
      <div
        className={`absolute hidden inset-0  !opacity-30 group-hover:opacity-100 transition-opacity duration-300`}
      />

      <div className="absolute -top-4 -left-4">
        <PokemonTypeBadge type={raid.type} className="" />
      </div>

      <div
        className={`absolute inset-0`}
        style={{
          background: `linear-gradient(90deg, var(--color-pk-${raid.type}) 0%, transparent 60%)`,
        }}
      ></div>

      {/* Content Container */}
      <div className="relative z-10 h-full flex flex-col items-start justify-start p-3 gap-1">
        {/* Name */}
        <StrokedText
          fontSize={24}
          strokeWidth={4}
          strokeColor="#3d2622"
          fillColor="#ffffff"
          className="text-center leading-tight drop-shadow-md"
        >
          {raid.name}
        </StrokedText>
      </div>

      <div className="absolute right-0 top-8">
        <PokemonSprite
          pokemon={{ base_pokemon_name: bossPokeNames[0] ?? "" }}
          animation="Idle"
          direction={Direction.SW}
          className=" z-11"
          style={{ scale: 2 * getPokemonSpriteScale(bossPokeNames[0] ?? "") }}
        />
      </div>

      <div className="absolute right-12 top-8">
        <PokemonSprite
          pokemon={{ base_pokemon_name: bossPokeNames[1] ?? "" }}
          animation="Idle"
          direction={Direction.SW}
          className=" z-10"
          style={{ scale: 2 * getPokemonSpriteScale(bossPokeNames[1] ?? "") }}
        />
      </div>

      <div className="absolute right-24 top-8">
        <PokemonSprite
          pokemon={{ base_pokemon_name: bossPokeNames[2] ?? "" }}
          animation="Idle"
          direction={Direction.SW}
          className=" z-9"
          style={{ scale: 2 * getPokemonSpriteScale(bossPokeNames[2] ?? "") }}
        />
      </div>

      <div className="absolute right-36 top-8">
        <PokemonSprite
          pokemon={{ base_pokemon_name: bossPokeNames[3] ?? "" }}
          animation="Idle"
          direction={Direction.SW}
          className=" z-9"
          style={{ scale: 2 * getPokemonSpriteScale(bossPokeNames[3] ?? "") }}
        />
      </div>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-pk-yellow-3 rounded-full flex items-center justify-center shadow-md z-20 animate-bounce">
          <span className="text-pk-gray-1 text-xs font-bold">✓</span>
        </div>
      )}
    </button>
  );
};
